<template>
  <div class="pieces-block">
    <div class="label required fs-xl">{{ title }}</div>

    <DeclarationData v-if="outsideEu" />

    <easy-data-table
      ref="piecesTable"
      table-class-name="assignment-pieces-table"
      :headers="headers"
      :items="pieces"
      :prevent-context-menu-row="false"
      hide-footer
      no-hover
    >
      <template #header="header">
        <div
          v-if="tooltipText(header.value)"
          v-b-tooltip="{ title: tooltipText(header.value), customClass: 'detailed-text-tooltip', variant: 'info' }"
        >
          {{ header.text }}
          <i class="fa fa-question-circle" />
        </div>
        <div v-else>
          {{ header.text }}
        </div>
      </template>

      <template #item="{ column, item }: { id: any; column: any; item: AssignmentPiece }">
        <div v-if="column === 'remove'">
          <button class="delete-piece-cta" @click="removePiece(item)"><mdicon name="TrashCanOutline" /></button>
        </div>
        <div v-else-if="column === 'origin_country_code'">
          <CountryPicker
            :vinput-id="`origin-country-${pieces.indexOf(item)}`"
            name="Origin"
            placeholder="Origin"
            :value="item.origin_country_code"
            :required="true"
            @change="updateOriginCountry($event, item)"
          />
        </div>
        <div v-else-if="column === 'ddsReference'" class="dds-reference-cell">
          <Field
            v-slot="{ field, errors }"
            :name="`pieces.${pieces.indexOf(item)}.ddsReference`"
            label="DDS reference"
            :rules="{ regex: /^[A-Za-z0-9]{1,35}$/ }"
            :model-value="item.ddsReference || ''"
          >
            <input
              v-bind="field"
              class="table-input"
              type="text"
              maxlength="35"
              autocomplete="off"
              :aria-label="`DDS reference for item ${pieces.indexOf(item) + 1} (optional)`"
              :aria-invalid="errors.length > 0"
              :aria-describedby="`shipment-dds-help dds-error-${pieces.indexOf(item)}`"
              @input="updatePiece($event, item, column)"
            />
            <span :id="`dds-error-${pieces.indexOf(item)}`" class="text-danger" role="alert">
              {{ errors.length ? 'Use 1–35 letters or digits, without spaces.' : '' }}
            </span>
          </Field>
        </div>
        <div v-else>
          <Field
            v-slot="{ field, errors }"
            :name="column"
            :rules="getFieldRules(column)"
            :model-value="item[column]"
            :validate-on-blur="true"
          >
            <div v-b-tooltip="errorTooltipOptions(errors)" class="h-55">
              <div
                class="wrap-table-input no-wrap"
                :class="{ validation: errors.length > 0, fatal: errors.length > 0 }"
              >
                <input
                  v-bind="field"
                  :value="displayValue(item[column], column)"
                  class="table-input"
                  :type="inputType(column)"
                  @input="updatePiece($event, item, column)"
                />
              </div>
            </div>
          </Field>
        </div>
      </template>
    </easy-data-table>

    <p v-if="ddsReferencesAvailable" id="shipment-dds-help" class="small mt-2">
      Only add a DDS reference for goods that need one. Use the reference from your declaration or supplier.
      Electronic transfer is currently supported only for DHL Express.
    </p>

    <div class="mt-3 d-flex justify-content-between pieces-actions">
      <div v-if="canAddPieces" class="add-piece-cta" @click="addNewPiece">
        <img src="@/assets/img/add-client-icon.svg" alt="" class="mr-2" />
        Add new piece
      </div>
      <div class="custom-alert">
        <small class="custom-alert-text ml-2">
          Dimensions and weight requirements are
          <router-link :to="{ name: PARCEL_REQUIREMENTS }" target="_blank"> here </router-link>
        </small>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import type { KeyAccessor } from '@/types/Helpers/Arrays'
import type { AssignmentCreationData, AssignmentPiece } from '@/types/Models/Assignment'
import { Field } from 'vee-validate'
import { defineComponent, type ComputedRef, computed } from 'vue'
import { defaultPiece } from '@/composables/Pieces/defaultPiece'
import { useValidationRules } from '@/composables/Pieces/useValidationRules'
import { BOX, PARCEL } from '@/const/DistributionChannels'
import { useAssignmentFormStore } from '@/store/assignmentForm'
import { PARCEL_REQUIREMENTS } from '@/types/router'
import EasyDataTable, { type Header } from 'vue3-easy-data-table'
import countriesEU from '@/assets/files/countriesEU.json'
import CountryPicker from '@/views/Components/Elements/Form/CountryPicker.vue'
import DeclarationData from '@/views/Components/Assignment/Pieces/DeclarationData.vue'

export default defineComponent({
  name: 'Pieces',
  components: {
    DeclarationData,
    EasyDataTable,
    Field,
    CountryPicker,
  },
  setup() {
    const assignmentFormStore = useAssignmentFormStore()

    const assignment: ComputedRef<AssignmentCreationData> = computed(() => assignmentFormStore.assignment)
    const ddsReferencesAvailable: ComputedRef<boolean> = computed(() => assignmentFormStore.ddsReferencesAvailable)
    const isInternational = computed(
      () => Boolean(assignment.value.item.country_code) && assignment.value.item.country_code !== 'SE',
    )
    const outsideEu = computed(
      () => Boolean(assignment.value.item.country_code) && !countriesEU.includes(assignment.value.item.country_code),
    )
    const title: ComputedRef<string> = computed(() =>
      !outsideEu.value ? 'Shipment data' : 'Shipment and customs declaration data',
    )
    const pieces: ComputedRef<AssignmentPiece[]> = computed(() => assignment.value.item.pieces)
    const recipientCountry: ComputedRef<string> = computed(() => assignment.value.item.country_code)
    const isBusinessRecipient: ComputedRef<boolean> = computed(() => Boolean(assignment.value.item.company_name))
    const distributionChannel: ComputedRef<string> = computed(() => assignment.value.distribution_channel)
    const canAddPieces: ComputedRef<boolean> = computed(
      () => Boolean(assignment.value.item.country_code) && assignment.value.item.country_code !== 'SE',
    )
    const canChangeQuantity: ComputedRef<boolean> = computed(() => [PARCEL, BOX].includes(distributionChannel.value))
    const headers: ComputedRef<Header[]> = computed(() => {
      let data: Header[] = [
        { text: 'Quantity', value: 'quantity', width: 88 },
        { text: 'Weight', value: 'weight', width: 72 },
        { text: 'Height', value: 'height', width: 72 },
        { text: 'Length', value: 'length', width: 72 },
        { text: 'Width', value: 'width', width: 72 },
        { text: 'Value', value: 'estimated_cost', width: 72 },
        { text: 'Description', value: 'description', width: 176 },
        { text: 'Commodity code', value: 'commodity_code', width: 128 },
        { text: 'Origin', value: 'origin_country_code', width: 144 },
        { text: '', value: 'remove', width: 48 },
      ]

      if (!canChangeQuantity.value) {
        data = data.filter((i) => !['quantity'].includes(i.value))
      }
      // International (non-SE) - show estimated_cost
      if (!isInternational.value) {
        data = data.filter((i) => !['estimated_cost'].includes(i.value))
      }
      // Non-EU - show commodity_code, origin_country_code
      if (!outsideEu.value) {
        data = data.filter((i) => !['commodity_code', 'origin_country_code'].includes(i.value))
      }

      if (pieces.value.length === 1) {
        data = data.filter((i) => i.value !== 'remove')
      }

      if (ddsReferencesAvailable.value) {
        const originIndex = data.findIndex((header) => header.value === 'origin_country_code')
        data.splice(originIndex + 1, 0, { text: 'DDS reference (optional)', value: 'ddsReference', width: 200 })
      }

      return data
    })

    const numberTypeColumns = ['weight', 'height', 'length', 'width', 'estimated_cost', 'quantity']
    const tooltips: KeyAccessor = {
      weight: 'Weight in GRAMS per one unit',
      height: 'Height in CM per one unit',
      width: 'Width in CM per one unit',
      length: 'Length in CM per one unit',
      description: 'Reference or shipment description, that will be placed on the shipping label',
    }

    const isNumeric = (column: string) => numberTypeColumns.includes(column)
    const inputType = (column: string) => (isNumeric(column) ? 'number' : 'text')
    const displayNumeric = (value: unknown) => (value === 0 ? '' : value)
    const displayValue = (value: unknown, column: string) => (isNumeric(column) ? displayNumeric(value) : value)
    const tooltipText = (field: string) => tooltips[field]
    const errorTooltipOptions = (errors: unknown[]) => ({
      title: errors.join(', '),
      variant: 'danger',
      customClass: 'piece-error-tooltip',
    })

    const addNewPiece = () => {
      assignmentFormStore.updatePieces({
        index: pieces.value.length,
        piece: { ...defaultPiece },
      })
    }

    const removePiece = (piece: AssignmentPiece) => {
      const index = pieces.value.findIndex((i) => i === piece)
      assignmentFormStore.updatePieces({ index })
    }

    const updatePiece = (event: Event, item: AssignmentPiece, column: string) => {
      const inputValue: string = (event.target as HTMLInputElement).value
      const newValue = numberTypeColumns.includes(column) ? Number(!inputValue ? 0 : inputValue) : String(inputValue)

      const index = pieces.value.findIndex((i) => i === item)
      const updatedPiece = { ...item, [column]: newValue } as AssignmentPiece
      assignmentFormStore.updatePieces({ index, piece: updatedPiece })
    }

    const updateOriginCountry = (value: string, item: AssignmentPiece) => {
      const index = pieces.value.findIndex((i) => i === item)
      const updatedPiece = { ...item, origin_country_code: value } as AssignmentPiece
      assignmentFormStore.updatePieces({ index, piece: updatedPiece })
    }

    const { getFieldRules } = useValidationRules({ distributionChannel, recipientCountry, isBusinessRecipient })

    return {
      title,
      ddsReferencesAvailable,
      pieces,
      headers,
      canAddPieces,
      numberTypeColumns,
      outsideEu,
      inputType,
      updatePiece,
      updateOriginCountry,
      addNewPiece,
      removePiece,
      tooltipText,
      displayValue,
      getFieldRules,
      displayNumeric,
      errorTooltipOptions,
      PARCEL_REQUIREMENTS,
    }
  },
})
</script>
